import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Spin } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  GoogleMap,
  CircleF,
  MarkerF,
  InfoWindowF,
  useLoadScript,
} from "@react-google-maps/api";
import { AppDispatch, RootState } from "../../../../app/store";
import {
  setChannel,
  setSource,
  setTemplate,
  selectBiz,
  setAreaCentre,
  selectAreaBiz,
  setAreaAddress,
  setRadiusKm,
  setIndustryGroup,
  setMcc,
  setManualTPV,
  setManualTxns,
  setManualTicket,
  setMinMonthlyVolume,
  fetchInsights,
  searchBusinessesThunk,
  searchAreaBusinessesThunk,
  searchPartnersThunk,
  setPartner,
  setCohort,
  selectTPV,
  selectTxns,
  selectAreaCentre,
  selectPeriod,
  setPeriod,
} from "../../pricingToolSlice";
import type { PartnerCohort } from "../../pricingToolSlice";
import {
  MAX_PERIOD_MONTHS,
  currentMonth,
  latestSelectableMonth,
  monthSpan,
  projectableDays,
} from "../../periodMonths";
import {
  TEMPLATE_OPTIONS,
  MCC_DATA,
  MCC_NAMES,
  INDUSTRY_GROUPS,
} from "../../pricingData";
import { Channel, VolSource, PricingTemplate } from "../../pricingData";
import { CONFIG } from "../../../../config";
import Seg from "../shared/Seg";
import Combo, { ComboOption } from "../shared/Combo";
import Spinner from "../shared/Spinner";
import AddressAutocomplete from "../shared/AddressAutocomplete";
import { eur } from "../shared/formatters";
import styles from "../../PricingTool.module.css";

const { RangePicker } = DatePicker;

// Stable reference so useLoadScript doesn't reload on every render.
const MAPS_LIBRARIES: "places"[] = ["places"];

// Pick a zoom so the radius circle's diameter is ~half the 150px-tall map. We
// snap to an integer zoom (raster maps round zoom anyway) and stay conservative
// so the whole ring is always visible, never overflowing the viewport.
function zoomForRadius(
  radiusKm: number,
  lat: number,
  mapHeightPx = 150
): number {
  const radiusMeters = radiusKm * 1000;
  const diameterPx = mapHeightPx * 0.5;
  const metersPerPixel = (2 * radiusMeters) / diameterPx;
  const z = Math.log2(
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / metersPerPixel
  );
  return Math.max(3, Math.min(18, Math.round(z)));
}

// Build industry options once
function buildIndustryOptions(): ComboOption[] {
  const byGroup: Record<string, string[]> = {};
  INDUSTRY_GROUPS.forEach((g) => (byGroup[g] = []));
  Object.entries(MCC_DATA).forEach(([code, g]) => {
    if (byGroup[g]) byGroup[g].push(code);
  });
  const items: ComboOption[] = [];
  INDUSTRY_GROUPS.forEach((g) => {
    items.push({
      key: "group:" + g,
      label: g,
      sub: "All categories",
      header: true,
    });
    byGroup[g]
      .sort((a, b) => Number(a) - Number(b))
      .forEach((code) =>
        items.push({
          key: "mcc:" + code,
          label: MCC_NAMES[code] || "MCC " + code,
          sub: code,
          indent: true,
        })
      );
  });
  return items;
}
const INDUSTRY_OPTIONS = buildIndustryOptions();

// Presets rather than a free-form box. The exact floor does not matter — what
// matters is separating merchants that trade from ones that barely do — and a
// typed figure invites tuning it until the average flatters the quote.
const MIN_VOLUME_OPTIONS = [
  { key: "0", label: "Any" },
  { key: "1000", label: "EUR 1k+" },
  { key: "5000", label: "EUR 5k+" },
  { key: "25000", label: "EUR 25k+" },
];

export default function VolumeBasis() {
  const dispatch = useDispatch<AppDispatch>();
  const s = useSelector((r: RootState) => r.pricingTool);
  const {
    channel,
    source,
    template,
    country,
    bizId,
    selectedBiz,
    areaCentre,
    areaBizId,
    areaBizCity,
    areaAddress,
    city,
    radiusKm,
    industryGroup,
    mcc,
    manualTPV,
    manualTxns,
    minMonthlyVolume,
    apiExcludedCount,
    apiTpv,
    apiTxns,
    fetchStatus,
    areaBusinesses,
    bizSearchResults,
    bizSearchStatus,
    areaBizSearchResults,
    areaBizSearchStatus,
    partnerId,
    partnerName,
    partnerSearchResults,
    partnerSearchStatus,
    cohort,
  } = s;

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: CONFIG.GOOGLE_MAPS.API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  // ── Debounced insight fetch ──
  const fetchTimer = useRef<ReturnType<typeof setTimeout>>();
  const fetchKey = [
    channel,
    source,
    bizId,
    country,
    city,
    industryGroup,
    mcc,
    radiusKm,
    areaCentre,
    areaBizId,
    areaAddress?.lat,
    areaAddress?.lng,
    partnerId,
    cohort,
    minMonthlyVolume,
    s.periodStart,
    s.periodEnd,
  ].join("|");

  useEffect(() => {
    if (source === "manual") return;
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(() => dispatch(fetchInsights()), 750);
    return () => {
      if (fetchTimer.current) clearTimeout(fetchTimer.current);
    };
  }, [fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced business search ──
  const bizTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleBizSearch = (q: string) => {
    if (bizTimer.current) clearTimeout(bizTimer.current);
    if (!q.trim()) return;
    bizTimer.current = setTimeout(
      () => dispatch(searchBusinessesThunk(q)),
      400
    );
  };

  // An empty query is a real search here — it asks for the first page of
  // partners, which is what the list should show when the picker opens.
  const partnerTimer = useRef<ReturnType<typeof setTimeout>>();
  const handlePartnerSearch = (q: string) => {
    if (partnerTimer.current) clearTimeout(partnerTimer.current);
    partnerTimer.current = setTimeout(
      () => dispatch(searchPartnersThunk(q)),
      400
    );
  };

  const ALL_PARTNERS = "__all__";
  const partnerOptions = [
    { key: ALL_PARTNERS, label: "All partners", sub: "No partner filter" },
    ...partnerSearchResults.filter((o) => o.key !== partnerId),
    ...(partnerId && partnerName
      ? [{ key: partnerId, label: partnerName, sub: "Selected" }]
      : []),
  ];

  const handlePartnerPick = (key: string) => {
    if (key === ALL_PARTNERS) {
      dispatch(setPartner(null));
      return;
    }
    const picked = partnerOptions.find((o) => o.key === key);
    dispatch(setPartner({ id: key, name: picked?.label ?? "Partner" }));
  };

  const areaBizTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleAreaBizSearch = (q: string) => {
    if (areaBizTimer.current) clearTimeout(areaBizTimer.current);
    if (!q.trim()) return;
    areaBizTimer.current = setTimeout(
      () => dispatch(searchAreaBusinessesThunk(q)),
      400
    );
  };

  const loading = fetchStatus === "loading";
  // Whole-period monthly averages (used for the "/ yr" projection).
  const bTpv = source === "manual" ? manualTPV : (apiTpv ?? 0);
  // Derived rather than stored: volume and count are the pair the model holds,
  // and a third stored figure could disagree with them.
  const manualTicket = manualTxns > 0 ? manualTPV / manualTxns : 0;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  // Month-aware values: reflect the month picked in the seasonality bar.
  const displayTpv = useSelector(selectTPV);
  const displayTxns = useSelector(selectTxns);

  const industryDisplay = mcc
    ? (MCC_NAMES[mcc] || "MCC " + mcc) + " · " + industryGroup
    : industryGroup;
  const industrySelectedKey = mcc ? "mcc:" + mcc : "group:" + industryGroup;

  const bizDisplayText = selectedBiz
    ? selectedBiz.displayName +
      (selectedBiz.city ? " · " + selectedBiz.city : "")
    : "Select business…";

  const areaBizDisplayText = areaBizId
    ? s.bizById[areaBizId]?.displayName || "Business selected"
    : "Select business for centre…";

  const areaCentreCoords = useSelector(selectAreaCentre);

  // ── Period ──
  const period = useSelector(selectPeriod);
  const latestMonth = latestSelectableMonth();
  const periodMonthCount = monthSpan(period);
  const isDefaultPeriod = s.periodStart == null && s.periodEnd == null;
  // The period runs into the month in progress, so its last bar is a rate
  // projected from the days so far rather than a settled month.
  const projectsCurrentMonth = period.end === currentMonth();
  const monthLabel = (month: string) => dayjs(`${month}-01`).format("MMM YYYY");
  const periodValue = useMemo(
    (): [Dayjs, Dayjs] => [
      dayjs(`${period.start}-01`),
      dayjs(`${period.end}-01`),
    ],
    [period.start, period.end]
  );

  // Grey out anything past the latest selectable month, and — once one end is
  // picked — everything more than a year from it, so the span cap is reached by
  // the picker refusing the click rather than by validating after the fact.
  const disabledMonth = (current: Dayjs, info: { from?: Dayjs }) => {
    if (!current) return false;
    if (current.format("YYYY-MM") > latestMonth) return true;
    if (!info?.from) return false;
    const span = Math.abs(current.diff(info.from, "month"));
    return span > MAX_PERIOD_MONTHS - 1;
  };

  const handlePeriodChange = (next: (Dayjs | null)[] | null) => {
    const from = next?.[0];
    const to = next?.[1];
    if (!from || !to) return;
    const picked = { start: from.format("YYYY-MM"), end: to.format("YYYY-MM") };
    // disabledMonth makes both unreachable; guard anyway so a span can never be
    // silently shortened the way the old 12-month truncation did.
    if (picked.end > latestMonth) return;
    if (monthSpan(picked) < 1 || monthSpan(picked) > MAX_PERIOD_MONTHS) return;
    dispatch(setPeriod(picked));
  };

  // Each returned business may carry lat/lng directly or as GeoJSON [lng, lat].
  const mapMarkers = areaBusinesses
    .map((b) => ({
      name: b.businessTradeName,
      lat: b.lat ?? b.location?.coordinates?.[1],
      lng: b.lng ?? b.location?.coordinates?.[0],
    }))
    .filter(
      (m): m is { name: string; lat: number; lng: number } =>
        m.lat != null && m.lng != null
    );

  // Frame the radius circle so it always fits the small map: derive a zoom level
  // from the radius (and the map height) instead of a fixed zoom.
  const mapZoom = zoomForRadius(radiusKm, areaCentreCoords?.lat ?? 0);

  // Marker whose name bubble is currently open.
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  useEffect(() => {
    setActiveMarker(null);
  }, [areaBusinesses]);

  // Stable center object so the map doesn't churn on every render.
  const lat = areaCentreCoords?.lat;
  const lng = areaCentreCoords?.lng;
  const mapCenter = useMemo(
    () => (lat != null && lng != null ? { lat, lng } : null),
    [lat, lng]
  );
  const mapRef = useRef<any>(null);
  // Overlays added before the map's first paint don't render (worsened by
  // React.StrictMode double-mount in dev), so gate them on tilesloaded.
  const [mapReady, setMapReady] = useState(false);

  const handleIndustryPick = (k: string) => {
    if (k.startsWith("group:")) dispatch(setIndustryGroup(k.slice(6)));
    else {
      const code = k.slice(4);
      dispatch(
        setMcc({ mcc: code, industryGroup: MCC_DATA[code] || industryGroup })
      );
    }
  };

  return (
    <>
      {/* Section header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: "rgba(0,0,0,0.88)",
            }}
          >
            Volume basis
          </h2>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12,
              color: "rgba(0,0,0,0.45)",
            }}
          >
            Pick a Klearly merchant, or model an area by location &amp;
            industry.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-end",
            flexShrink: 0,
          }}
        >
          <span className={styles.section_label} style={{ margin: 0 }}>
            CHANNEL
          </span>
          <Seg
            opts={[
              { key: "offline", label: "In-person" },
              { key: "online", label: "Online" },
            ]}
            active={channel}
            onSel={(k) => dispatch(setChannel(k as Channel))}
          />
        </div>
      </div>

      {/* Source selector */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <span className={styles.section_label} style={{ margin: 0 }}>
          HOW TO GET THE VOLUME
        </span>
        <Seg
          opts={[
            { key: "business", label: "Existing business" },
            { key: "area", label: "By area" },
            { key: "manual", label: "Manual" },
          ]}
          active={source}
          onSel={(k) => dispatch(setSource(k as VolSource))}
          sm
        />
      </div>

      {/* ── Period ── (manual volume is typed in, so it has no history to average) */}
      {source !== "manual" && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 6,
            }}
          >
            <span className={styles.section_label} style={{ margin: 0 }}>
              PERIOD
            </span>
            {!isDefaultPeriod && (
              <button
                type="button"
                className={styles.month_clear_btn}
                onClick={() => dispatch(setPeriod(null))}
              >
                Last 12 months ✕
              </button>
            )}
          </div>
          <RangePicker
            picker="month"
            allowClear={false}
            value={periodValue}
            format="MMM YYYY"
            style={{ width: "100%" }}
            disabledDate={disabledMonth}
            onChange={handlePeriodChange}
          />
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: "rgba(0,0,0,0.45)",
              marginTop: 8,
              lineHeight: 1.4,
            }}
          >
            {periodMonthCount === 1
              ? "Averaging a single month."
              : `Averaging ${periodMonthCount} months.`}{" "}
            Whole months, up to {MAX_PERIOD_MONTHS}, ending no later than{" "}
            {monthLabel(latestMonth)}.
            {projectsCurrentMonth &&
              ` ${monthLabel(period.end)} is still in progress — its first ${projectableDays()} days are scaled to a full-month rate, so read that month as a projection. Whole weeks only, so takings are not weighted towards the days of the week that happen to have passed.`}
          </span>
        </div>
      )}

      {/* ── Business source ── */}
      {source === "business" && (
        <div style={{ marginBottom: 16 }}>
          <span className={styles.section_label}>EXISTING BUSINESS</span>
          <Combo
            displayText={bizDisplayText}
            options={bizSearchResults}
            selectedKey={bizId ?? undefined}
            onPick={(k) => dispatch(selectBiz(k))}
            placeholder="Search Klearly businesses…"
            onSearch={handleBizSearch}
            loading={bizSearchStatus === "loading"}
          />
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: "rgba(0,0,0,0.45)",
              marginTop: 8,
              lineHeight: 1.4,
            }}
          >
            Volume inferred from this merchant's live transaction history.
            Metrics are read-only.
          </span>
        </div>
      )}

      {/* ── Area source ── */}
      {source === "area" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <span className={styles.section_label}>INDUSTRY</span>
            <Combo
              displayText={industryDisplay}
              options={INDUSTRY_OPTIONS}
              selectedKey={industrySelectedKey}
              onPick={handleIndustryPick}
              placeholder="Search category or industry group…"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <span className={styles.section_label}>PARTNER</span>
            <div style={{ marginBottom: 8 }}>
              <Seg
                sm
                opts={[
                  { key: "all", label: "All" },
                  { key: "partners", label: "With a partner" },
                  { key: "direct", label: "Direct only" },
                ]}
                active={cohort}
                onSel={(k) => dispatch(setCohort(k as PartnerCohort))}
              />
            </div>
            {/* Asking for merchants with no partner and for one partner's
                merchants are opposite questions, so only one picker is on
                screen at a time. */}
            {cohort !== "direct" && (
              <Combo
                displayText={partnerName ?? "All partners"}
                options={partnerOptions}
                selectedKey={partnerId ?? ALL_PARTNERS}
                onPick={handlePartnerPick}
                placeholder="Search partners…"
                onSearch={handlePartnerSearch}
                loading={partnerSearchStatus === "loading"}
              />
            )}
            <span
              style={{
                display: "block",
                fontSize: 11,
                color: "rgba(0,0,0,0.45)",
                marginTop: 8,
                lineHeight: 1.4,
              }}
            >
              {cohort === "direct"
                ? "Only merchants with no partner attached. Klearly keeps their whole fee, so there is no markup to take off."
                : partnerName
                  ? `Only ${partnerName}'s merchants in the area. The markup shown is this partner's contract.`
                  : cohort === "partners"
                    ? "Only merchants that have a partner, whichever one — so the markup shown is a blend of their contracts. Pick a partner to read one."
                    : "All merchants in the area, most of which belong to no partner — so any markup shown is a blend. Pick a partner to read one real contract."}
            </span>
          </div>

          <div style={{ marginBottom: 12 }}>
            <span className={styles.section_label}>MINIMUM VOLUME</span>
            <Seg
              sm
              opts={MIN_VOLUME_OPTIONS}
              active={String(minMonthlyVolume)}
              onSel={(k) => dispatch(setMinMonthlyVolume(Number(k)))}
            />
            <span
              style={{
                display: "block",
                fontSize: 11,
                color: "rgba(0,0,0,0.45)",
                marginTop: 8,
                lineHeight: 1.4,
              }}
            >
              {minMonthlyVolume > 0
                ? `Merchants under ${eur(minMonthlyVolume)} a month are left out of every figure below.${
                    apiExcludedCount
                      ? ` ${apiExcludedCount} set aside here.`
                      : apiExcludedCount === 0
                        ? " None in this area were under it."
                        : ""
                  }`
                : "Every merchant that traded at all is counted, including one taking a few hundred euros a month — which pulls the averages down."}
            </span>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 6,
              }}
            >
              <span className={styles.section_label} style={{ margin: 0 }}>
                AREA CENTRE
              </span>
              <Seg
                opts={[
                  { key: "address", label: "Address" },
                  { key: "business", label: "Business" },
                ]}
                active={areaCentre === "business" ? "business" : "address"}
                onSel={(k) =>
                  dispatch(setAreaCentre(k as "business" | "address"))
                }
                sm
              />
            </div>
            {areaCentre !== "business" && (
              <AddressAutocomplete
                ready={mapsLoaded}
                value={areaAddress?.label ?? ""}
                placeholder="Search an address, place or postcode…"
                onSelect={(r) => dispatch(setAreaAddress(r))}
              />
            )}
            {areaCentre === "business" && (
              <Combo
                displayText={areaBizDisplayText}
                options={areaBizSearchResults}
                selectedKey={areaBizId ?? undefined}
                onPick={(k) => dispatch(selectAreaBiz(k))}
                placeholder="Search business to centre on…"
                onSearch={handleAreaBizSearch}
                loading={areaBizSearchStatus === "loading"}
              />
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 6,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span className={styles.section_label} style={{ margin: 0 }}>
                  RADIUS
                </span>
                <span
                  style={{ fontSize: 12, color: "#6B4EFF", fontWeight: 600 }}
                >
                  {radiusKm} km
                </span>
              </div>
              <input
                type="range"
                min={1}
                max={25}
                step={1}
                value={radiusKm}
                onChange={(e) =>
                  dispatch(setRadiusKm(parseInt(e.target.value)))
                }
                className={styles.range_input}
                style={{ background: "#e6e6ff", width: "100%" }}
              />
              <span
                style={{
                  display: "block",
                  fontSize: 11,
                  color: "rgba(0,0,0,0.45)",
                  marginTop: 8,
                  lineHeight: 1.4,
                }}
              >
                {!mapCenter
                  ? areaCentre === "business" && areaBizId
                    ? "This business has no mapped location yet — centre on an address instead."
                    : "Pick an area centre to model a radius."
                  : mapMarkers.length > 0
                    ? `${mapMarkers.length} merchants within ${radiusKm} km`
                    : `${radiusKm} km radius around ${areaCentre === "business" ? areaBizCity : (areaAddress?.label ?? "selected address")}`}
              </span>
            </div>
            <div className={styles.map_container}>
              {mapsLoaded && mapCenter ? (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={mapZoom}
                  onLoad={(m) => {
                    mapRef.current = m;
                  }}
                  onUnmount={() => {
                    mapRef.current = null;
                    setMapReady(false);
                  }}
                  onTilesLoaded={() => setMapReady(true)}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    gestureHandling: "greedy",
                    clickableIcons: false,
                  }}
                >
                  {mapReady && (
                    <>
                      <CircleF
                        center={mapCenter}
                        radius={radiusKm * 1000}
                        options={{
                          strokeColor: "#6B4EFF",
                          strokeOpacity: 0.9,
                          strokeWeight: 2,
                          fillColor: "#6B4EFF",
                          fillOpacity: 0.18,
                          clickable: false,
                          zIndex: 1,
                        }}
                      />
                      {mapMarkers.map((m, i) => (
                        <MarkerF
                          key={i}
                          position={{ lat: m.lat, lng: m.lng }}
                          title={m.name}
                          icon={{
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 5,
                            fillColor: "#6B4EFF",
                            fillOpacity: 1,
                            strokeColor: "#fff",
                            strokeWeight: 1.6,
                          }}
                          zIndex={2}
                          onClick={() => setActiveMarker(i)}
                        />
                      ))}
                      {activeMarker != null && mapMarkers[activeMarker] && (
                        <InfoWindowF
                          position={{
                            lat: mapMarkers[activeMarker].lat,
                            lng: mapMarkers[activeMarker].lng,
                          }}
                          onCloseClick={() => setActiveMarker(null)}
                          options={{ pixelOffset: new google.maps.Size(0, -8) }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "rgba(0,0,0,0.85)",
                              padding: "2px 4px",
                              maxWidth: 200,
                            }}
                          >
                            {mapMarkers[activeMarker].name}
                          </div>
                        </InfoWindowF>
                      )}
                      <MarkerF
                        position={mapCenter}
                        zIndex={3}
                        title="Area centre"
                        icon={{
                          path: "M12 0C7.6 0 4 3.6 4 8c0 5.4 8 16 8 16s8-10.6 8-16c0-4.4-3.6-8-8-8z",
                          fillColor: "#1f1147",
                          fillOpacity: 1,
                          strokeColor: "#fff",
                          strokeWeight: 1.6,
                          scale: 1.5,
                          anchor: new google.maps.Point(12, 24),
                        }}
                      />
                    </>
                  )}
                </GoogleMap>
              ) : (
                <div className={styles.map_placeholder}>
                  {mapsLoaded ? (
                    <span
                      style={{
                        fontSize: 11,
                        color: "rgba(0,0,0,0.45)",
                        textAlign: "center",
                        padding: "0 10px",
                      }}
                    >
                      No area centre
                    </span>
                  ) : (
                    <Spin size="small" />
                  )}
                </div>
              )}
              <div className={styles.map_badge}>
                {mapMarkers.length > 0
                  ? `${mapMarkers.length} businesses · `
                  : ""}
                {radiusKm} km
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Manual source ── */}
      {source === "manual" && (
        <div style={{ marginBottom: 16 }}>
          <div>
            <span className={styles.section_label}>INDUSTRY · CARD MIX</span>
            <Combo
              displayText={industryDisplay}
              options={INDUSTRY_OPTIONS}
              selectedKey={industrySelectedKey}
              onPick={handleIndustryPick}
              placeholder="Search category or industry group…"
            />
          </div>
        </div>
      )}

      {/* ── Metric cards ── */}
      {loading ? (
        <div className={styles.metric_grid}>
          <div className={styles.metric_loader}>
            <Spinner size={20} />
            <span style={{ fontSize: 12, color: "#6B4EFF", fontWeight: 500 }}>
              Fetching analytics…
            </span>
          </div>
          <div className={styles.metric_loader}>
            <Spinner size={20} />
          </div>
        </div>
      ) : source === "manual" ? (
        <div className={styles.metric_grid}>
          <div
            className={`${styles.metric_card} ${styles.metric_card_editable}`}
          >
            <div className={styles.metric_label}>Volume / mo</div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  paddingRight: 4,
                  color: "rgba(0,0,0,0.45)",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                €
              </span>
              <input
                type="number"
                className={styles.metric_input}
                value={Math.round(manualTPV)}
                step={1000}
                onChange={(e) =>
                  dispatch(
                    setManualTPV(Math.max(0, parseFloat(e.target.value) || 0))
                  )
                }
              />
            </div>
            <div className={styles.metric_sub}>editable</div>
          </div>
          <div
            className={`${styles.metric_card} ${styles.metric_card_editable}`}
          >
            <div className={styles.metric_label}>Transactions / mo</div>
            <input
              type="number"
              className={styles.metric_input}
              value={Math.round(manualTxns)}
              step={50}
              onChange={(e) =>
                dispatch(
                  setManualTxns(Math.max(1, parseFloat(e.target.value) || 1))
                )
              }
            />
            <div className={styles.metric_sub}>editable</div>
          </div>
          {/* The third of three figures, of which only two are independent.
              Typing here holds the transaction count and moves the volume —
              a merchant who knows their average ticket usually knows roughly
              how many sales they make, and far less often the monthly total. */}
          <div
            className={`${styles.metric_card} ${styles.metric_card_editable}`}
          >
            <div className={styles.metric_label}>Average ticket</div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  paddingRight: 4,
                  color: "rgba(0,0,0,0.45)",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                €
              </span>
              <input
                type="number"
                className={styles.metric_input}
                value={round2(manualTicket)}
                step={1}
                min={0}
                onChange={(e) =>
                  dispatch(
                    setManualTicket(
                      Math.max(0, parseFloat(e.target.value) || 0)
                    )
                  )
                }
              />
            </div>
            <div className={styles.metric_sub}>sets volume</div>
          </div>
        </div>
      ) : (
        <div className={styles.metric_grid}>
          <div className={styles.metric_card}>
            <div className={styles.metric_label}>Volume / mo</div>
            <div className={styles.metric_value}>
              {displayTpv > 0 ? eur(displayTpv) : "—"}
            </div>
            <div className={styles.metric_sub}>
              {bTpv > 0
                ? eur(bTpv * 12) + " / yr"
                : bizId || source === "area"
                  ? "No data"
                  : "Select a business"}
            </div>
          </div>
          <div className={styles.metric_card}>
            <div className={styles.metric_label}>Transactions / mo</div>
            <div className={styles.metric_value}>
              {displayTxns > 0
                ? Math.round(displayTxns).toLocaleString("en-US")
                : "—"}
            </div>
          </div>
          <div className={styles.metric_card}>
            <div className={styles.metric_label}>Average ticket</div>
            <div className={styles.metric_value}>
              {displayTxns > 0 ? eur(displayTpv / displayTxns) : "—"}
            </div>
            <div className={styles.metric_sub}>volume / transactions</div>
          </div>
        </div>
      )}

      {/* Template selector (offline only — shown here below metrics so PricingPerGroup can be thin) */}
      {channel === "offline" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 10,
          }}
        >
          <span className={styles.section_label} style={{ margin: 0 }}>
            PRICING TEMPLATE
          </span>
          <Seg
            opts={TEMPLATE_OPTIONS.map((t) => ({
              key: t.key,
              label: t.label,
              sub: t.sub,
            }))}
            active={template}
            onSel={(k) => dispatch(setTemplate(k as PricingTemplate))}
            sm
          />
        </div>
      )}
    </>
  );
}
